/**
 * Cloud saves, the shared vocabulary. The game stays local-first: localStorage is the store it
 * reads and writes, synchronously, exactly as before; this layer only mirrors four of its keys to
 * the server the page came from, under a save code, when that server offers it.
 */

/** The keys that are mirrored - and nothing else ever is. server/saveApi.mjs holds the same list. */
export const CLOUD_KEY = {
  profile: 'nightdrop.profile',
  suspend: 'nightdrop.suspend',
  settings: 'nightdrop.settings.v1',
  bindings: 'nightdrop.bindings.v1',
} as const;

export type CloudKey = (typeof CLOUD_KEY)[keyof typeof CLOUD_KEY];

export const CLOUD_KEYS: readonly CloudKey[] = Object.values(CLOUD_KEY);

/**
 * The campaign: the profile and the mission suspended inside it. They are one unit - a suspended
 * mission only makes sense in the profile that deployed it - so they are kept or replaced together.
 */
export const CAMPAIGN_KEYS: readonly CloudKey[] = [CLOUD_KEY.profile, CLOUD_KEY.suspend];

/** Where this layer keeps its own bookkeeping (never mirrored: it holds the code). */
export const STATE_KEY = 'nightdrop.cloud.v1';
/** A save that lost a conflict or was replaced by another code is kept under this prefix + a timestamp. */
export const BACKUP_PREFIX = 'nightdrop.backup.';

export function isCloudKey(key: string): key is CloudKey {
  return (CLOUD_KEYS as readonly string[]).includes(key);
}

/** The slice of `localStorage` this layer needs; tests hand it a Map-backed object. `setItem` may throw (quota). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `window.fetch`, narrowed to how it is called here, so node's `fetch` and a test double both fit. */
export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

export interface LocationLike {
  /** The API lives at the root of the origin the page came from: same-origin by construction. */
  readonly origin: string;
  reload(): void;
}

/** Injected so tests can move time by hand instead of waiting for it. */
export interface TimerLike {
  set(run: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface CloudDeps {
  /** Null where the browser gives the page no storage: cloud saves are then off. */
  storage: KeyValueStore | null;
  fetch: FetchLike;
  location: LocationLike;
  now?: () => number;
  timers?: TimerLike;
  /** Entropy for a new save code; `crypto.getRandomValues` unless a test says otherwise. */
  randomBytes?: (count: number) => Uint8Array;
}

/** What the server keeps under one code. */
export interface SaveDocument {
  revision: number;
  /** Server time of the last write, ms since the epoch. */
  updatedAt: number;
  keys: Partial<Record<CloudKey, string>>;
}

/**
 * `off`: this server has no save API (or there is no server) - the game is exactly the local game.
 * `idle`: in step with the server. `syncing`: a request is in flight. `offline`: the server did
 * not answer; changes wait on this device and are retried. `conflict-resolved`: two devices had
 * both played; one save was kept and the other backed up (the message says which).
 * `paused`: another device is saving under this code right now, or the server refused a save;
 * nothing more is sent this session and the next start of the game settles it.
 */
export type CloudStateName = 'off' | 'idle' | 'syncing' | 'offline' | 'conflict-resolved' | 'paused';

export interface CloudStatus {
  state: CloudStateName;
  /** One line for the player, in the menus' voice; '' when there is nothing to add. */
  message: string;
  /** This device's clock at the last exchange the server answered; null before the first. */
  lastSyncedAt: number | null;
}

/** What is persisted under `STATE_KEY`. */
export interface CloudState {
  code: string;
  /** The server revision this device last saw; 0 = nothing uploaded yet. */
  revision: number;
  /** Keys changed here and not yet acknowledged by the server. */
  dirty: CloudKey[];
  /** This device's clock at the latest unsent change: the last tie-break of the conflict rule. */
  dirtyAt: number | null;
  syncedAt: number | null;
  /** Fingerprint of each key's value as of the last sync, so "the server changed it" can be told from "only I did". */
  base: Partial<Record<CloudKey, string>>;
  /** Backup keys written by this layer, oldest first, so the old ones can be pruned. */
  backups: string[];
}

export interface CloudSave {
  /** Asks the server once whether it keeps saves. Anything but a clear yes turns the layer off until reload. */
  probe(): Promise<boolean>;
  /** Brings localStorage up to date BEFORE the game reads it. Never rejects. */
  pullAtBoot(): Promise<CloudStatus>;
  enabled(): boolean;
  /** "This key may just have been written": marks it for upload if its value really changed. */
  touch(key: string): void;
  /** Send what is waiting now (the page is being hidden or closed). Fire and forget. */
  flush(): void;
  /** Resolves when no request is in flight: for tests, and for `useCode`. */
  whenIdle(): Promise<void>;
  dispose(): void;

  /* ---- what the CLOUD SAVE screen sees (structurally a `CloudPort`, src/ui/screens/cloud/cloudPort.ts) */
  status(): CloudStatus;
  /** Canonical form (27 characters); null while cloud saves are off. */
  code(): string | null;
  /** Null when the save under `code` now replaces the local one; otherwise why not, for the player. */
  useCode(code: string): Promise<string | null>;
  /** Reload the page: the profile, settings and bindings are read once at start-up. */
  restart(): void;
  subscribe(listener: () => void): () => void;
}
